/**
 * Pure calculation/validation logic for `AllowanceDrawer`, extracted per this
 * repo's component-testability rule (co-located `*_validation.ts` for a
 * >150-line component, `CLAUDE.md`'s "Component Testability + Stories").
 *
 * Owns:
 *  - `AllowanceRateKind` -- the drawer's 4-way rate-type selector. A UI-level
 *    superset of the backend's 3-way `rate_type` enum (`'fixed' | 'per_hour' |
 *    'percentage'`, `payroll.createPayItem`/`updatePayItem`,
 *    `apps/api/src/trpc/routers/payroll.ts`): `'manager_input'` represents
 *    `rate_type: 'fixed'` with no `rate_cents` recorded -- the real "manager
 *    enters the amount at pay-run time" fallback, not a distinct backend enum
 *    value (see `wagepro_store_data_centre.ts`'s module docblock, "Rule/basis
 *    derivation" section).
 *  - `AllowanceRateFormState` -- the drawer's raw editable state for whichever
 *    rate kind is selected (an amount in minor units for `fixed`/`per_hour`, a
 *    percent STRING for `percentage` -- mirrors `data_centre_loan_section.tsx`'s
 *    own percent-as-text pattern so the field can hold an in-progress,
 *    not-yet-valid keystroke like `"1."`).
 *  - `allowanceRateFormStateFromRule` -- reverse-maps an existing
 *    `Allowance.rule` (what the read adapter derived) back into editable form
 *    state, for edit-mode prefill.
 *  - `isAllowanceRateFormValid` / `toAllowanceRateDraft` -- validates the
 *    current form state and converts it into the data layer's
 *    `AllowanceRateDraft` (the write-path seam `wagepro_store_data_centre.ts`
 *    consumes), matching `payroll.createPayItem`/`updatePayItem`'s zod input
 *    exactly (mirrors `wagepro_store_payroll_settings.ts`'s own "mirrors the
 *    zod input exactly" convention).
 *  - `isAllowanceRateSectionValid` / `resolveAllowanceRateDraft` (#22820
 *    F1/F2) -- the untouched-rate-section escape hatch. A rate section the
 *    user never interacted with (`rateTouched: false`) is always valid
 *    regardless of what `isAllowanceRateFormValid` says about its prefilled
 *    amount, and its save resolves to the ORIGINAL `AllowanceRateSnapshot`
 *    (`../../data/types.ts`) verbatim via `AllowanceRateDraft`'s
 *    `'unchanged'` kind, rather than `toAllowanceRateDraft`'s normal
 *    re-derivation from the current (merely prefilled) form state. See
 *    `wagepro_store_data_centre.ts`'s module docblock, "Untouched rate
 *    section round-trips unchanged" section, for the full rationale.
 */
import type { AllowanceRateSnapshot, AllowanceRule } from '../../data/types';
import type { AllowanceRateDraft } from '../../data/wagepro_store_data_centre';
import {
  formatBasisPointsAsPercentInput,
  parsePercentToBasisPoints,
} from '../../data/wagepro_store_payroll_settings';

export type AllowanceRateKind =
  | 'fixed'
  | 'per_hour'
  | 'percentage'
  | 'manager_input';

/** Selector option order, top to bottom. */
export const ALLOWANCE_RATE_KINDS: readonly AllowanceRateKind[] = [
  'fixed',
  'per_hour',
  'percentage',
  'manager_input',
];

/**
 * Matches `payroll.createPayItem`/`updatePayItem`'s `rate_bps` ceiling
 * exactly (`z.number().int().min(0).max(100_000)`,
 * `apps/api/src/trpc/routers/payroll.ts`) -- a client-side rejection and the
 * server's own rejection must always agree.
 */
export const MAX_RATE_BPS = 100_000;

export interface AllowanceRateFormState {
  rateKind: AllowanceRateKind;
  /** Minor units (cents). Only meaningful when `rateKind` is `'fixed'`/`'per_hour'`. */
  amountCents: number;
  /** Raw percent text (e.g. `"8.5"`). Only meaningful when `rateKind` is `'percentage'`. */
  ratePercentText: string;
}

export function defaultAllowanceRateFormState(): AllowanceRateFormState {
  return { rateKind: 'manager_input', amountCents: 0, ratePercentText: '0' };
}

/**
 * Reverse-maps an existing allowance's derived `rule` into editable form
 * state, for edit-mode prefill. `'hourly_multiple'` is never produced by the
 * real read adapter (see `wagepro_store_data_centre.ts`'s module docblock --
 * no backing column exists to prefill an amount from) so it falls back to the
 * same safe "no rate recorded" default `'manager_input'` uses, rather than
 * fabricating a value.
 */
export function allowanceRateFormStateFromRule(
  rule: AllowanceRule,
): AllowanceRateFormState {
  switch (rule.kind) {
    case 'fixed_monthly':
      return {
        rateKind: 'fixed',
        amountCents: rule.amount,
        ratePercentText: '0',
      };
    case 'hourly_rate':
      return {
        rateKind: 'per_hour',
        amountCents: rule.amount,
        ratePercentText: '0',
      };
    case 'percent_of_basic':
      return {
        rateKind: 'percentage',
        amountCents: 0,
        ratePercentText: formatBasisPointsAsPercentInput(rule.rate),
      };
    case 'manager_input':
    case 'hourly_multiple':
      return defaultAllowanceRateFormState();
  }
}

/**
 * Whether the current form state has everything needed to save: an
 * amount-bearing rate type needs a positive amount/rate entered (mirrors this
 * file's name-validation pattern -- a zero/blank value is treated as "not yet
 * entered", not a valid zero-rate allowance). `manager_input` never needs a
 * value.
 */
export function isAllowanceRateFormValid(
  state: AllowanceRateFormState,
): boolean {
  switch (state.rateKind) {
    case 'fixed':
    case 'per_hour':
      return state.amountCents > 0;
    case 'percentage': {
      const bps = parsePercentToBasisPoints(state.ratePercentText);
      return bps !== null && bps > 0 && bps <= MAX_RATE_BPS;
    }
    case 'manager_input':
      return true;
  }
}

/**
 * Converts a validated form state into the data layer's `AllowanceRateDraft`.
 * Callers must gate on {@link isAllowanceRateFormValid} first (mirrored here
 * by a `?? 0` fallback so an in-progress invalid percent string can never
 * produce `NaN`/`undefined` on the wire).
 */
export function toAllowanceRateDraft(
  state: AllowanceRateFormState,
): AllowanceRateDraft {
  switch (state.rateKind) {
    case 'fixed':
      return { kind: 'fixed', rate_cents: state.amountCents };
    case 'per_hour':
      return { kind: 'per_hour', rate_cents: state.amountCents };
    case 'percentage':
      return {
        kind: 'percentage',
        rate_bps: parsePercentToBasisPoints(state.ratePercentText) ?? 0,
      };
    case 'manager_input':
      return { kind: 'manager_input' };
  }
}

/**
 * Whether Save should be blocked on the rate section (#22820 F2). A rate
 * section the user has not touched since the drawer opened is ALWAYS valid,
 * no matter what {@link isAllowanceRateFormValid} says about its prefilled
 * amount — an existing record with an explicit `rate_cents: 0` (or
 * `rate_bps: 0`/`null`) prefills into an amount-bearing kind with a
 * technically-invalid zero amount, and that must never block a pure rename.
 * Once touched, falls back to the normal rule: the user is actively choosing
 * a rate now, so it has to be a valid one.
 */
export function isAllowanceRateSectionValid(
  state: AllowanceRateFormState,
  rateTouched: boolean,
): boolean {
  if (!rateTouched) return true;
  return isAllowanceRateFormValid(state);
}

/**
 * Resolves the `AllowanceRateDraft` a save actually sends (#22820 F1): an
 * untouched rate section on an EDIT (a real `originalSnapshot` present) sends
 * that ORIGINAL triad back verbatim via the `'unchanged'` kind — never
 * re-derived from `state`, which is only the as-loaded prefill and has
 * already lost the fixed-vs-per_hour distinction for a null-rate record (see
 * `AllowanceRateSnapshot`'s doc comment, `../../data/types.ts`). Falls back
 * to the normal `toAllowanceRateDraft` mapping whenever the section HAS been
 * touched, or there is no original to preserve (a brand-new allowance, where
 * `originalSnapshot` is `undefined` because there is nothing to round-trip).
 */
export function resolveAllowanceRateDraft(
  state: AllowanceRateFormState,
  rateTouched: boolean,
  originalSnapshot: AllowanceRateSnapshot | undefined,
): AllowanceRateDraft {
  if (!rateTouched && originalSnapshot !== undefined) {
    return {
      kind: 'unchanged',
      rate_type: originalSnapshot.rate_type,
      rate_cents: originalSnapshot.rate_cents,
      rate_bps: originalSnapshot.rate_bps,
    };
  }
  return toAllowanceRateDraft(state);
}
